import { noddiAsync, URLKeys } from 'noddi-async';
import {
  reserveStationarySlotWithReplaceStrategy,
  type StationarySlotHoldCredentials
} from './reserveStationarySlotStrategy';

export type { StationarySlotHoldCredentials };

/** Atomic hold: time window, bay, credentials, TTL. Shared customer + partner. */
export type StationarySlotHold = {
  startsAt: string;
  endsAt: string;
  workstationId: number;
  slotId: number;
  ticket: string | null;
  secret: string | null;
  reservedUntil: string;
  /** When the hold was taken, so a countdown can show how much of the TTL is left. Missing on holds persisted before this field existed. */
  reservedFrom?: string;
};

export type ReserveStationarySlotInput = {
  start: string;
  end: string;
  garageId: number;
  salesItemIds?: number[];
  previousHold?: StationarySlotHoldCredentials | null;
  /** `previousHold` was cancelled to make room and the new reserve still failed. */
  onPreviousHoldLost?: () => void;
  workstationId: number;
};

type ReserveApiResult = {
  slotId: number;
  reservedUntil: string;
  reservation?: { ticket?: string | null; secret?: string | null } | null;
};

/** Bay cancel only (expiry / store clear / replace). No reserve mutation. */
export function useCancelStationaryHold() {
  const { mutateAsync: cancelTimeWindow, isPending } = noddiAsync.usePost({
    type: URLKeys.postCancelSlotReservation
  });

  const cancelHoldStrict = async (hold: StationarySlotHoldCredentials | null | undefined) => {
    if (!hold) {
      return;
    }
    await cancelTimeWindow({
      slotId: hold.slotId,
      reservationSecret: hold.secret ?? undefined,
      reservationTicket: hold.ticket ?? undefined
    });
  };

  const cancelHold = async (hold: StationarySlotHoldCredentials | null | undefined) => {
    try {
      await cancelHoldStrict(hold);
    } catch {
      // Best-effort — hold may already be expired or reaped by TTL.
    }
  };

  return { cancelHold, cancelHoldStrict, isPending };
}

/** Cancel + reserve lifecycle. Prefers reserve-first so a failed replace never drops the live hold. */
export function useReserveStationarySlot() {
  const { cancelHold, cancelHoldStrict, isPending: isCancelPending } = useCancelStationaryHold();
  const { mutateAsync: reserveTimeWindow, isPending: isReservePending } = noddiAsync.usePost({
    type: URLKeys.postReserveSlot
  });

  const toHold = (input: ReserveStationarySlotInput, data: ReserveApiResult): StationarySlotHold => ({
    startsAt: input.start,
    endsAt: input.end,
    workstationId: input.workstationId,
    slotId: data.slotId,
    ticket: data.reservation?.ticket ?? null,
    secret: data.reservation?.secret ?? null,
    reservedUntil: data.reservedUntil,
    // Taken at the response, not the request: the TTL the backend returns starts when it granted the hold.
    reservedFrom: new Date().toISOString()
  });

  const reserveBody = (input: ReserveStationarySlotInput) => ({
    start: input.start,
    end: input.end,
    garageId: input.garageId,
    salesItemIds: input.salesItemIds
  });

  const reserveSlot = async (input: ReserveStationarySlotInput): Promise<StationarySlotHold> =>
    reserveStationarySlotWithReplaceStrategy({
      previousHold: input.previousHold,
      reserve: async () => {
        const { data } = await reserveTimeWindow(reserveBody(input));
        return toHold(input, data);
      },
      cancelHold: async (hold) => {
        await cancelHoldStrict(hold);
      },
      onPreviousHoldLost: input.onPreviousHoldLost
    });

  return {
    reserveSlot,
    cancelHold,
    isPending: isReservePending || isCancelPending
  };
}
